import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../../PageHeader/PageHeader';
import { HitApi } from '../../../Api/ApiHit';
import { deleteDevice, searchDevice } from '../../../constant/Constant';
import { useDispatch, useSelector } from 'react-redux';
import { setApiJson } from '../../../redux/Actions/ApiAction';
import AppTable from '../../../components/AppTable/AppTable';
import { setPagination } from '../../../redux/Actions/TableAction';
import DeleteButton from '../../../components/Delete/DeleteButton';
import EditButton from '../../../components/Edit/EditButton';
import toast from 'react-hot-toast';
import TableSearch from '../../../components/TableSearch/TableSearch';
import { getSelectedUnit } from '../../../storage/Storage';
import ViewButton from '../../../components/AppButton/ViewButton';

function DeviceMaster() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const ApiReducer = useSelector((state) => state.ApiReducer);
    const TableDataReducer = useSelector((state) => state.TableReducer);
    const [optionSelect, setOptionSelect] = useState('');
    const [valueSearched, setValueSearched] = useState('');
    const [isSearching, setIsSearching] = useState(false);

    useEffect(() => {
        fetchDevices();
    }, []);

    const fetchDevices = (searchKey, searchValue) => {
        setIsSearching(true);

        const searchObj = {
            unitIds: getSelectedUnit()?._id
        };

        // Only add search key/value if both are provided
        if (searchKey && searchValue) {
            searchObj[searchKey] = searchValue;
        }

        const json = {
            page: 1,
            limit: 10,
            search: searchObj
        };

        HitApi(json, searchDevice).then((res) => {
            if (res?.statusCode === 200) {
                const deviceData = Array.isArray(res?.data?.docs) ? res?.data?.docs : [];
                dispatch(setApiJson(deviceData));
            } else {
                dispatch(setApiJson([]));
                toast.error('Failed to fetch devices. Please try again.');
            }
        }).catch(error => {
            console.error('Error fetching devices:', error);
            dispatch(setApiJson([]));
            toast.error('Error fetching devices. Please try again.');
        }).finally(() => {
            setIsSearching(false);
        });
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleEdit = (row) => {
        navigate(`/registerdevice/edit/${row._id}`);
    };

    const handleDelete = (row) => {
        if (window.confirm(`Are you sure you want to delete ${row.deviceName}?`)) {
            const json = {
                _id: row?._id
            };
            HitApi(json, deleteDevice).then((res) => {
                if (res?.statusCode === 200) {
                    toast.success('Device deleted successfully!');
                    fetchDevices();
                } else {
                    toast.error('Failed to delete device. Please try again.');
                }
            }).catch(error => {
                console.error('Error deleting device:', error);
                toast.error('Error occurred while deleting device. Please try again.');
            });
        }
    };

    const handleView = (row) => {
        navigate(`/registerdevice/ads/${row._id}`);
    };

    // Search functionality
    const handleSearch = () => {
        if (!optionSelect) {
            toast.error('Please select a search option');
            return;
        }
        if (!valueSearched || valueSearched.trim() === '') {
            toast.error('Please enter a search value');
            return;
        }
        fetchDevices(optionSelect, valueSearched.trim());
    };

    // Search options based on table keys
    const options = [
        { label: 'Device Name', value: 'deviceName' },
        { label: 'Device Code', value: 'deviceCode' },
        { label: 'Device Type', value: 'deviceType' },
        { label: 'Status', value: 'status' },
    ];

    const renderDeviceType = (deviceType) => {
        if (!deviceType) return 'N/A';

        const typeConfig = {
            'Mobile': { bg: 'bg-blue-100', text: 'text-blue-800' },
            'Television': { bg: 'bg-purple-100', text: 'text-purple-800' },
            'Screen': { bg: 'bg-green-100', text: 'text-green-800' },
            'Billboard': { bg: 'bg-orange-100', text: 'text-orange-800' }
        };

        const config = typeConfig[deviceType] || { bg: 'bg-gray-100', text: 'text-gray-800' };

        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
                {deviceType}
            </span>
        );
    };

    const renderOrientation = (isVertical, resolution) => {
        const isVerticalOrientation = isVertical === true || isVertical === 'true';
        const bgColor = isVerticalOrientation ? "bg-pink-100" : "bg-blue-100";
        const textColor = isVerticalOrientation ? "text-pink-800" : "text-blue-800";
        const label = isVerticalOrientation ? "Vertical" : "Horizontal";

        return (
            <div className="space-y-1">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
                    {label}
                </span>
                {resolution && (
                    <div className="text-xs text-gray-600 mt-1">
                        {resolution}
                    </div>
                )}
            </div>
        );
    };

    const renderStatus = (status) => {
        const isActive = status?.toLowerCase() === 'active';
        const bgColor = isActive ? "bg-green-100" : "bg-red-100";
        const textColor = isActive ? "text-green-800" : "text-red-800";
        const icon = isActive ? "✓" : "✗";

        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
                {icon} {status || 'N/A'}
            </span>
        );
    };

    const renderLocation = (lat, lng) => {
        if (!lat || !lng) {
            return (
                <span className="text-xs text-gray-400">No location</span>
            );
        }

        return (
            <div className="text-xs">
                <div className="text-gray-600">📍 Lat: {parseFloat(lat).toFixed(4)}</div>
                <div className="text-gray-600">Lng: {parseFloat(lng).toFixed(4)}</div>
            </div>
        );
    };

    const baseIndex = (TableDataReducer.pagination.page - 1) * TableDataReducer.pagination.limit;

    const tableHeaders = [
        {
            title: "S.No",
            key: "serialNumber",
            width: "60px",
            align: "center",
            render: (_, row, index) => {
                const serialNumber = baseIndex + index + 1;
                return (
                    <div className="text-center">
                        <span className="text-sm font-medium text-gray-900">{serialNumber}</span>
                    </div>
                );
            }
        },
        {
            title: "Device Details",
            key: "deviceName",
            render: (value, row) => (
                <div>
                    <div className="font-medium text-gray-900">{value || 'N/A'}</div>
                    <div className="text-xs text-gray-500">Code: {row.deviceCode || 'N/A'}</div>
                </div>
            )
        },
        {
            title: "Device Type",
            key: "deviceType",
            render: (value) => renderDeviceType(value)
        },
        {
            title: "Orientation & Size",
            key: "orientation",
            render: (_, row) => renderOrientation(row.isVertical, row.resolution)
        },
        {
            title: "Location",
            key: "location",
            render: (_, row) => renderLocation(row.lat, row.lng)
        },
        {
            title: "Status",
            key: "status",
            render: (value) => renderStatus(value)
        },
        {
            title: "Actions",
            key: "actions",
            align: "center",
            render: (value, row) => (
                <div className="flex justify-center space-x-2">
                    <ViewButton
                        onClick={() => handleView(row)}
                        itemName="advertisements"
                    />
                    <EditButton
                        onEdit={() => handleEdit(row)}
                        itemName="device"
                        tooltip="Edit Device"
                    />
                    <DeleteButton
                        onDelete={() => handleDelete(row)}
                        itemName="device"
                        tooltip="Delete Device"
                    />
                </div>
            )
        }
    ];



    const handleClearSearch = () => {
        setOptionSelect('');
        setValueSearched('');
        fetchDevices();
    };

    return (
        <div className="p-5">
            <PageHeader
                title={'Device Management'}
                description={'View and manage registered devices for advertisements'}
            />

            <AppTable
                TH={tableHeaders}
                TD={Array.isArray(ApiReducer.apiJson) ? ApiReducer.apiJson : []}
                isLoading={isSearching}
                emptyMessage="No devices found"
                buttonText="Register Device"
                navigateTo="/registerdevice/add"
                TableSearch={
                    <TableSearch
                        options={options}
                        onClick={handleSearch}
                        optionSelect={optionSelect}
                        setOptionSelect={setOptionSelect}
                        setValueSearched={setValueSearched}
                        valueSearched={valueSearched}
                        onClear={handleClearSearch}
                    />
                }
            />
        </div>
    );
}

export default DeviceMaster;
